/* eslint-disable import/extensions */
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { RadioGroup, TextField } from '@masfront/common-b2c'
import { Form, Field, FastField, Formik } from 'formik'

import {
  Box,
  makeStyles,
  Grid,
  Typography,
  FormControlLabel,
  Radio,
  Collapse,
} from '@material-ui/core'

import AddressInput from './AddressInput'
import AddressPickerSchemas from './AddressPicker.schemas'
import useAddressPicker from './useAddressPicker'

import { AddressesOptions, AddressType } from '../models/address.model'
import { setAddressOwnerAction } from '../store/address.reducer'
import { selectAddressByType, selectAddressOwner } from '../store/address.selectors'

import { RootState } from '~/core/store/root.reducer'
import { LineTypes } from '~/tariff/model/tariff.model'

export interface AddressPickerProps {
  id: string
  addressType?: AddressType
}

const useStyles = makeStyles(() => ({
  fullWidth: {
    width: '100%',
  },
}))

function AddressPicker({ id, addressType = AddressType.SIM }: AddressPickerProps) {
  const { disableFields } = useAddressPicker(id, addressType)
  const styles = useStyles()

  return (
    <>
      <Grid container spacing={5}>
        <Grid xs={7} item>
          <AddressInput id={id} addressType={addressType} />
        </Grid>
        <Grid xs={2} item>
          <FastField
            component={TextField}
            name="number"
            label="Número"
            variant="outlined"
            inputProps={{
              maxLength: 4,
            }}
            disabled={disableFields}
          />
        </Grid>
        <Grid xs={1} item>
          <FastField
            name="floor"
            data-hook={`floor-${id}`}
            variant="outlined"
            label="Piso"
            component={TextField}
          />
        </Grid>
        <Grid xs={2} item>
          <FastField
            name="letter"
            data-hook={`letter-${id}`}
            label="Letra"
            variant="outlined"
            component={TextField}
          />
        </Grid>
      </Grid>
      <Grid container spacing={5}>
        <Grid xs={2} item>
          <FastField
            component={TextField}
            name="postalCode"
            label="CP"
            variant="outlined"
            inputProps={{
              maxLength: 5,
            }}
            disabled={disableFields}
          />
        </Grid>
        <Grid xs={2} item>
          <FastField
            component={TextField}
            name="otherInfo"
            label="Otra Información"
            variant="outlined"
          />
        </Grid>
        <Grid xs={4} item>
          <FastField
            className={styles.fullWidth}
            component={TextField}
            name="town"
            label="Localidad"
            variant="outlined"
            disabled={disableFields}
          />
        </Grid>
        <Grid xs={4} item>
          <FastField
            className={styles.fullWidth}
            component={TextField}
            name="province"
            label="Provincia"
            variant="outlined"
            disabled={disableFields}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default function AddressPickerForm({
  id,
  addressType = AddressType.SIM,
}: AddressPickerProps) {
  const address = useSelector((state: RootState) => selectAddressByType(state, id, addressType))
  const dispatch = useDispatch()
  const addressOwner = useSelector((state: RootState) => selectAddressOwner(state, id, addressType))
  const styles = useStyles()
  // TODO make same address
  return (
    <Formik
      initialValues={{ ...address.data, addressOwner }}
      initialStatus={{}}
      enableReinitialize
      validateOnMount
      validationSchema={AddressPickerSchemas}
      validateOnBlur
      onSubmit={(_values, formik) => {
        formik.setSubmitting(false)
        formik.validateForm()
      }}
    >
      <Form autoComplete="off" className={styles.fullWidth} data-hook={`address-picker-${id}`}>
        <Box px={6}>
          {id !== LineTypes.MAIN && id !== LineTypes.ALL ? (
            <Box>
              <Field
                component={RadioGroup}
                name="addressOwner"
                data-hook="addressOwner"
                onClick={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value === addressOwner || !event.target.value) return
                  dispatch(
                    setAddressOwnerAction({
                      id,
                      addressType,
                      addressOwner:
                        event.target.value === AddressesOptions.SAME
                          ? AddressesOptions.SAME
                          : AddressesOptions.OTHER,
                    }),
                  )
                }}
              >
                <Box display="flex">
                  <FormControlLabel
                    value={AddressesOptions.SAME}
                    control={<Radio color="primary" />}
                    label={
                      <Typography variant="subtitle2">
                        Misma dirección que la línea principal
                      </Typography>
                    }
                    data-hook={`address-type-${AddressesOptions.SAME}-${id}`}
                  />
                  <FormControlLabel
                    value={AddressesOptions.OTHER}
                    control={<Radio color="primary" />}
                    label={<Typography variant="subtitle2">Otra dirección</Typography>}
                    data-hook={`address-type-${AddressesOptions.OTHER}-${id}`}
                  />
                </Box>
              </Field>
              <Collapse in={addressOwner === AddressesOptions.OTHER}>
                <Box mt={8}>
                  <AddressPicker id={id} addressType={addressType} />
                </Box>
              </Collapse>
            </Box>
          ) : (
            <AddressPicker id={id} addressType={addressType} />
          )}
        </Box>
      </Form>
    </Formik>
  )
}
